import { useEffect, useState } from 'react';
import { useBusinessNav } from '@/hooks/useBusinessNav';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Switch } from '@/components/ui/switch';
import { Label } from '@/components/ui/label';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { useApp } from '@/components/AppLayout';
import { taxApi, type Tax } from '@/lib/api';
import { ArrowLeft, Percent } from 'lucide-react';

function TaxesContent() {
  const { nav } = useBusinessNav();
  const { currentBusiness, isViewOnly } = useApp();
  const [taxes, setTaxes] = useState<Tax[]>([]);
  const [showArchived, setShowArchived] = useState(false);

  useEffect(() => {
    if (currentBusiness) {
      loadTaxes();
    }
  }, [currentBusiness, showArchived]);

  const loadTaxes = async () => {
    if (!currentBusiness) return;
    const response = await taxApi.list(currentBusiness.id, showArchived);
    if (response.data) {
      setTaxes(response.data);
    }
  };

  const handleArchiveTax = async (id: string, isCurrentlyArchived: boolean) => {
    const response = isCurrentlyArchived
      ? await taxApi.unarchive(id)
      : await taxApi.archive(id);
    if (response.error) {
      alert(response.error);
    } else {
      loadTaxes();
    }
  };

  return (
    <div className="px-6 pt-6 pb-6">
      <div className="mb-6">
        <Button
          variant="ghost"
          size="sm"
          onClick={() => nav('/settings')}
          className="gap-2 text-muted-foreground hover:text-foreground -ml-2"
        >
          <ArrowLeft className="h-4 w-4" />
          Back to Settings
        </Button>
      </div>

      <div className="space-y-1 mb-8">
        <h1 className="text-2xl font-semibold tracking-tight flex items-center gap-2">
          <Percent className="h-6 w-6" />
          Tax Rates
        </h1>
        <p className="text-sm text-muted-foreground">
          Manage tax rates for your invoices and quotes.
        </p>
      </div>

      <Card>
        <CardContent className="pt-6">
          <div className="flex justify-between items-center mb-4">
            <div className="flex items-center gap-2">
              <Switch
                id="show-archived"
                checked={showArchived}
                onCheckedChange={setShowArchived}
              />
              <Label htmlFor="show-archived" className="text-sm text-muted-foreground">
                Show archived
              </Label>
            </div>
            {!isViewOnly && (
              <Button onClick={() => nav('/taxes/add')} size="sm">
                Add Tax
              </Button>
            )}
          </div>

          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="border-b">
                  <th className="text-left py-3 px-4 text-sm font-medium">Name</th>
                  <th className="text-left py-3 px-4 text-sm font-medium">Rate</th>
                  <th className="text-left py-3 px-4 text-sm font-medium">Type</th>
                  <th className="text-left py-3 px-4 text-sm font-medium">Parts</th>
                  <th className="text-left py-3 px-4 text-sm font-medium">Labor</th>
                  <th className="text-right py-3 px-4 text-sm font-medium">Actions</th>
                </tr>
              </thead>
              <tbody>
                {taxes.length === 0 ? (
                  <tr>
                    <td colSpan={6} className="text-center py-8 text-muted-foreground">
                      No taxes found. Add your first tax to get started.
                    </td>
                  </tr>
                ) : (
                  taxes.map((tax) => (
                    <tr
                      key={tax.id}
                      className={`border-b hover:bg-muted/50 cursor-pointer ${tax.is_archived ? 'opacity-50' : ''}`}
                      onClick={() => nav(`/taxes/${tax.id}`)}
                    >
                      <td className="py-3 px-4 font-medium">
                        {tax.name}
                        {tax.is_archived && <span className="ml-2 text-xs text-muted-foreground">(archived)</span>}
                      </td>
                      <td className="py-3 px-4 text-muted-foreground">{tax.rate}%</td>
                      <td className="py-3 px-4 text-muted-foreground capitalize">{tax.tax_type}</td>
                      <td className="py-3 px-4 text-muted-foreground">
                        {tax.apply_to_all_parts ? (
                          <span className="inline-flex items-center gap-1 text-green-600">
                            <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                            </svg>
                          </span>
                        ) : (
                          <span className="text-muted-foreground">-</span>
                        )}
                      </td>
                      <td className="py-3 px-4 text-muted-foreground">
                        {tax.apply_to_all_labor ? (
                          <span className="inline-flex items-center gap-1 text-green-600">
                            <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                            </svg>
                          </span>
                        ) : (
                          <span className="text-muted-foreground">-</span>
                        )}
                      </td>
                      <td className="py-3 px-4 text-right">
                        <DropdownMenu>
                          <DropdownMenuTrigger asChild onClick={(e) => e.stopPropagation()}>
                            <Button variant="ghost" size="sm">
                              <svg className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 5v.01M12 12v.01M12 19v.01M12 6a1 1 0 110-2 1 1 0 010 2zm0 7a1 1 0 110-2 1 1 0 010 2zm0 7a1 1 0 110-2 1 1 0 010 2z" />
                              </svg>
                            </Button>
                          </DropdownMenuTrigger>
                          <DropdownMenuContent align="end">
                            <DropdownMenuItem
                              onClick={(e) => {
                                e.stopPropagation();
                                handleArchiveTax(tax.id, tax.is_archived);
                              }}
                            >
                              {tax.is_archived ? 'Unarchive' : 'Archive'}
                            </DropdownMenuItem>
                          </DropdownMenuContent>
                        </DropdownMenu>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}

export function Taxes() {
  return <TaxesContent />;
}
